'use client';

import moment from 'moment-timezone';
import React from 'react';
import MessageItem from 'src/components/job-slide-out/components/nav-table/tenant-lula-chat/message-item';
import type { TenantContactMessage } from 'src/types';

export type TenantTextsStatus = 'idle' | 'loading' | 'loaded' | 'error';

interface TextThreadProps {
	messages: TenantContactMessage[];
	contactName: string;
	jobTimezone: string;
}

interface ThreadRow {
	key: string;
	message: TenantContactMessage;
	// Non-empty when this row should render a date divider AFTER its bubble.
	// With column-reverse the divider ends up visually ABOVE the next (older) group.
	dayLabel: string;
}

// messages are newest-first. Divider goes AFTER the last message of each day group in DOM
// so that column-reverse places it ABOVE the group visually (as a day header).
const buildRows = (
	messages: TenantContactMessage[],
	jobTimezone: string,
): ThreadRow[] => {
	const seen = new Map<string, number>();
	const dayLabelOf = (msg: TenantContactMessage): string =>
		msg.created_date_utc && jobTimezone
			? moment
					.utc(msg.created_date_utc, 'YYYY-MM-DD HH:mm:ss')
					.tz(jobTimezone)
					.format('dddd, MMM D')
			: '';
	return messages.map((message, index) => {
		const dayLabel = dayLabelOf(message);

		const nextMessage = messages[index + 1];
		const nextDayLabel = nextMessage ? dayLabelOf(nextMessage) : '';

		// Emit divider after this message when the next is a different day or this is the last.
		const showDivider =
			dayLabel !== '' &&
			(nextMessage === undefined || dayLabel !== nextDayLabel);

		const base = `${message.created_date}|${message.direction}|${message.text}`;
		const occurrence = seen.get(base) ?? 0;
		seen.set(base, occurrence + 1);

		return {
			key: `${base}#${occurrence}`,
			message,
			dayLabel: showDivider ? dayLabel : '',
		};
	});
};

const TextThread: React.FC<TextThreadProps> = ({
	messages,
	contactName,
	jobTimezone,
}) => {
	return (
		<>
			{buildRows(messages, jobTimezone).map((row) => (
				<React.Fragment key={row.key}>
					<MessageItem
						message={row.message}
						contactName={contactName}
						jobTimezone={jobTimezone}
					/>
					{row.dayLabel ? (
						<div className="tenant-texts__date">
							<span>{row.dayLabel}</span>
						</div>
					) : null}
				</React.Fragment>
			))}
		</>
	);
};

TextThread.displayName = 'TextThread';

export default TextThread;
